import React, { useState } from "react";
import BaseDialog from "@/components/Modals/BaseDialog";
import { Button } from "@/components/shadcn/button";
import { Textarea } from "@/components/shadcn/textarea";
import { Alert, AlertDescription } from "@/components/shadcn/alert";
import {
  Loader2,
  AlertCircle,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import { client } from "@/generated/client.gen";
import { useSpotUser } from "@/utils/spotUser";

interface ConvertPlaywrightResponse {
  playbook: Record<string, unknown>;
  steps_count: number;
  warnings?: string[];
}

interface ConvertPlaywrightModalProps {
  open: boolean;
  onClose: () => void;
  onConvert: (playbook: any) => void;
}

export function ConvertPlaywrightModal({
  open,
  onClose,
  onConvert,
}: ConvertPlaywrightModalProps) {
  const { spotGroup } = useSpotUser();
  const groupId = spotGroup.id;
  const [playwrightCode, setPlaywrightCode] = useState("");
  const [isConverting, setIsConverting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);

  const handleConvert = async () => {
    if (!playwrightCode.trim()) {
      setError("Please paste your browser script first");
      return;
    }

    setIsConverting(true);
    setError(null);
    setSuccess(null);
    setWarnings([]);

    try {
      const { data } = (await client.post({
        url: `/groups/${groupId}/playbook/convert-playwright` as never,
        body: { code: playwrightCode },
        headers: { "Content-Type": "application/json" },
        responseType: "json",
      })) as { data: ConvertPlaywrightResponse | undefined };

      if (!data) {
        setError("Failed to convert script");
        return;
      }

      if (data.steps_count === 0) {
        setError(
          "No browser actions found in the script. Make sure to paste page.* or browser.* actions."
        );
        return;
      }

      if (data.warnings && data.warnings.length > 0) {
        setWarnings(data.warnings);
      }

      setSuccess(
        `Successfully converted ${data.steps_count} ${data.steps_count === 1 ? "step" : "steps"}!`
      );

      onConvert(data.playbook);
    } catch (err: any) {
      const errorMessage =
        err.detail || err.message || "Failed to convert code";
      setError(errorMessage);
    } finally {
      setIsConverting(false);
    }
  };

  const handleClose = () => {
    setPlaywrightCode("");
    setError(null);
    setSuccess(null);
    setWarnings([]);
    onClose();
  };

  return (
    <BaseDialog
      isOpen={open}
      onOpenChange={handleClose}
      title="Import from Script"
      size="4xl"
      overflowVisible={false}
      disableScroll={false}
    >
      <div className="space-y-5">
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Paste browser automation code generated by recording tools like
            Playwright, Puppeteer, or Selenium. The converter will automatically
            clean up and optimize the steps.
          </p>
          <p className="text-xs text-muted-foreground">
            Supports Python and JavaScript syntax
          </p>
        </div>

        <div className="space-y-3">
          <label
            htmlFor="playwright-code"
            className="text-sm font-medium block"
          >
            Browser Script
          </label>
          <Textarea
            id="playwright-code"
            value={playwrightCode}
            onChange={(e) => setPlaywrightCode(e.target.value)}
            placeholder={`Paste your recorded browser script here, for example:

page.goto("https://example.com")
page.get_by_placeholder("Username").fill("user@example.com")
page.get_by_role("button", name="Login").click()
page.get_by_text("Dashboard").click()`}
            className="font-mono text-xs min-h-[320px] resize-none"
          />
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert className="border-green-500 bg-green-50 dark:bg-green-950/20 dark:border-green-800">
            <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
            <AlertDescription className="text-green-800 dark:text-green-300">
              {success}
            </AlertDescription>
          </Alert>
        )}

        {warnings.length > 0 && (
          <Alert className="border-amber-500 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800">
            <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            <AlertDescription className="text-amber-800 dark:text-amber-300">
              <p className="font-medium mb-1.5">
                {warnings.length}{" "}
                {warnings.length === 1 ? "line was" : "lines were"} not fully
                converted:
              </p>
              <ul className="list-disc pl-4 space-y-0.5">
                {warnings.map((w, i) => (
                  <li key={i} className="text-xs">
                    {w}
                  </li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        <div className="flex justify-end gap-2 pt-4">
          {success ? (
            <Button type="button" onClick={handleClose}>
              Close
            </Button>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isConverting}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleConvert}
                disabled={isConverting || !playwrightCode.trim()}
              >
                {isConverting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Converting...
                  </>
                ) : (
                  "Convert & Import"
                )}
              </Button>
            </>
          )}
        </div>
      </div>
    </BaseDialog>
  );
}
